import React, { useState, useEffect, useMemo } from 'react';
import { Sticker as StickerIcon } from 'lucide-react';

// Marker used to identify sticker content in note text
export const STICKER_MARKER = '[Sticker]';

// Parse sticker payload from raw text
export const parseStickerPayload = (rawPayload) => {
  if (!rawPayload) return null;

  const trimmed = rawPayload.trim();
  const endIndex = trimmed.lastIndexOf('}');
  if (!trimmed.startsWith('{') || endIndex === -1) {
    return null;
  }

  const jsonFragment = trimmed.slice(0, endIndex + 1);

  try {
    const parsed = JSON.parse(jsonFragment);
    if (parsed && parsed.type === 'line_sticker') {
      return parsed;
    }
  } catch (error) {
    console.warn('Failed to parse sticker payload:', rawPayload, error);
  }

  return null;
};

// Component to render LINE sticker preview
export const StickerPreview = ({ data }) => {
  // Extract data with safe defaults
  const {
    setUrl,
    stickerId,
    assets = {}
  } = data || {};

  const previewUrl = assets?.previewUrl || assets?.staticUrl || assets?.popupUrl || assets?.animationUrl;
  const previewUrlFallbacks = assets?.previewUrlFallbacks || assets?.staticUrlFallbacks || [];
  const soundUrl = assets?.soundUrl;

  // State to track current URL index and image error
  // Hooks must be called unconditionally at the top level
  const [currentUrlIndex, setCurrentUrlIndex] = useState(0);
  const [imageError, setImageError] = useState(false);

  // Build array of URLs to try (primary + fallbacks)
  const urlList = useMemo(() => {
    const urls = [];
    if (previewUrl) urls.push(previewUrl);
    if (previewUrlFallbacks && previewUrlFallbacks.length > 0) {
      urls.push(...previewUrlFallbacks);
    }
    return urls;
  }, [previewUrl, previewUrlFallbacks]);

  const currentUrl = urlList[currentUrlIndex] || null;

  // Handle image load error - try next fallback URL
  const handleImageError = () => {
    if (currentUrlIndex < urlList.length - 1) {
      // Try next fallback URL
      setCurrentUrlIndex(currentUrlIndex + 1);
      setImageError(false);
    } else {
      // All URLs failed
      setImageError(true);
    }
  };

  // Reset when data changes
  useEffect(() => {
    setCurrentUrlIndex(0);
    setImageError(false);
  }, [data]);

  // Early return after all hooks
  if (!data) return null;

  return (
    <span className="inline-flex items-center space-x-2 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-md px-2 py-1 ml-2">
      {currentUrl && !imageError ? (
        <img
          src={currentUrl}
          alt={`LINE sticker ${stickerId || ''}`}
          className="w-12 h-12 rounded-md object-contain border border-green-200 dark:border-green-600 bg-white"
          loading="lazy"
          onError={handleImageError}
          key={currentUrlIndex} // Force re-render when URL changes
        />
      ) : (
        <StickerIcon className="w-10 h-10 text-green-600 dark:text-green-400" />
      )}
      <span className="flex flex-col">
        <span className="text-xs font-semibold text-green-700 dark:text-green-300 uppercase tracking-wide leading-tight">
          LINE sticker
        </span>
        <span className="text-xs text-green-600 dark:text-green-200 leading-tight">
          ID: {stickerId || 'N/A'}
        </span>
        {soundUrl && (
          <a
            href={soundUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 dark:text-blue-300 hover:underline leading-tight"
          >
            Play sound
          </a>
        )}
      </span>
    </span>
  );
};
